import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { 
  ArrowLeft, Upload, Trash2, GripVertical,
  ExternalLink, Save, X, Loader2, Palette, Check, MessageSquare, Send, Search, Link as LinkIcon, Plus, Eye, Share2, Newspaper, Image, ChevronDown, Paintbrush, Type, Gift, DollarSign, Clock, Music, Timer, Mail, Tag, Heart, CreditCard, Download, AlertTriangle, Edit2, Monitor, Smartphone,
  Settings as SettingsIcon, User as UserIcon, Sticker as StickerCardIcon, Crown as CrownIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { socialPlatforms, validateUrl } from '../utils/socialPlatforms';
import { isEmbeddableUrl, getEmbedUrl, getEmbedLabel } from '../utils/embedUtils';
import { THEME_LIST, getTheme } from '../utils/themes';
import { FONT_OPTIONS, ANIMATION_OPTIONS, BUTTON_STYLE_OPTIONS, LAYOUT_OPTIONS, ALL_FONTS_URL, getFontById, getAvatarShape } from '../utils/customisation';
import { WALLPAPER_OPTIONS, getWallpaperCSS, getWallpaperElements } from '../utils/wallpapers';
import { useAuth } from '../App';
import { api, getImageUrl } from '../utils/api';
import { MonkeyLogo as PreviewMonkeyLogo } from '../components/MonkeyLogo';
import V2CustomisationBlock from '../components/V2CustomisationBlock';
import { StickerEditor, StickerLayer } from '../components/StickerEditor';

import { PlatformRow } from '../components/settings/PlatformRow';
import { RecentTipsList } from '../components/settings/RecentTipsList';
import { LinkRichEditor } from '../components/settings/LinkRichEditor';
import { CustomLinkForm } from '../components/settings/CustomLinkForm';
import { ProfilePreview } from '../components/settings/ProfilePreview';

/* ─── Main Settings Component ─── */
// Mirrors the backend's free tiers in routes/profile.py — everything else is Pro.
const FREE_OPTIONS = {
  themes: ['default', 'light', 'midnight', 'ocean'],
  fonts: ['inter'],
  animations: ['none'],
  buttonStyles: ['rounded'],
  layouts: ['centered'],
  wallpapers: ['none'],
};

const ProPill = ({ className = 'top-1.5 right-1.5' }) => (
  <span className={`absolute ${className} z-10 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#0055FF] text-white shadow-sm pointer-events-none`}>
    <CrownIcon className="w-2.5 h-2.5" /> Pro
  </span>
);

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const isProUser = !!user?.subscription?.is_pro;

  const [formData, setFormData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    theme: user?.theme || 'default',
    font: user?.font || 'inter',
    animation: user?.animation || (user?.subscription?.is_pro ? 'fade' : 'none'),
    button_style: user?.button_style || 'rounded',
    layout: user?.layout || 'centered',
    accent_color: user?.accent_color || '',
    wallpaper: user?.wallpaper || 'none',
    profile_music_url: user?.profile_music_url || '',
    countdown_date: user?.countdown_date || '',
    countdown_label: user?.countdown_label || '',
    email_signup_enabled: user?.email_signup_enabled || false,
    email_signup_title: user?.email_signup_title || '',
    email_signup_subtitle: user?.email_signup_subtitle || '',
    email_signup_button: user?.email_signup_button || '',
    tip_jar_enabled: user?.tip_jar_enabled || false,
    tip_jar_paypal: user?.tip_jar_paypal || '',
    tip_jar_title: user?.tip_jar_title || '',
    tip_jar_subtitle: user?.tip_jar_subtitle || '',
    tip_jar_currency: user?.tip_jar_currency || 'EUR',
    tip_jar_show_balance: user?.tip_jar_show_balance || false,
    tip_jar_balance: typeof user?.tip_jar_balance === 'number' ? user.tip_jar_balance : 0,
    tip_jar_goal: user?.tip_jar_goal ?? null,
    // v2 customisation
    avatar_shape: user?.avatar_shape || 'round',
    link_card_style: user?.link_card_style || 'classic',
    bg_effect: user?.bg_effect || 'none',
    hide_branding: user?.hide_branding || false,
    orbit_enabled: user?.orbit_enabled || false,
    orbit_speed: user?.orbit_speed || 'normal',
    orbit_direction: user?.orbit_direction || 'cw',
    orbit_platforms: user?.orbit_platforms || [],
    orbit_spotlight_color: user?.orbit_spotlight_color || '',
    custom_wallpaper_url: user?.custom_wallpaper_url || null,
    custom_wallpaper_mode: user?.custom_wallpaper_mode || 'cover',
    custom_wallpaper_opacity: typeof user?.custom_wallpaper_opacity === 'number' ? user.custom_wallpaper_opacity : 100,
    custom_wallpaper_blur: typeof user?.custom_wallpaper_blur === 'number' ? user.custom_wallpaper_blur : 0,
    custom_wallpaper_scale: typeof user?.custom_wallpaper_scale === 'number' ? user.custom_wallpaper_scale : 100,
    stickers: user?.stickers || []
  });

  const pickOption = (field, value, isProOption, featureName) => {
    if (isProOption && !isProUser) {
      window.dispatchEvent(new CustomEvent('upgrade-required', { detail: { message: `${featureName} is a Pro feature. Upgrade at /pricing` } }));
      return;
    }
    setFormData({ ...formData, [field]: value });
  };

  const [profilePicture, setProfilePicture] = useState(user?.profilePicture || null);
  const [links, setLinks] = useState(user?.links || []);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ profilePicture: false });
  const profilePicRef = useRef(null);
  const [contactMsg, setContactMsg] = useState('');
  const [messages, setMessages] = useState([]);
  const [hasUnreadMsg, setHasUnreadMsg] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const messagesEndRef = useRef(null);

  // Platform link states
  const [platformInputs, setPlatformInputs] = useState({});
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [platformSearch, setPlatformSearch] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  // Universal preview mode: 'mobile' (phone frame) | 'desktop' (browser frame).
  // Kept separate from `showPreview` so the existing StickerEditor wiring stays untouched.
  const [previewMode, setPreviewMode] = useState('mobile');
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showThemes, setShowThemes] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);

  // Diary state
  const [diaryPosts, setDiaryPosts] = useState([]);
  const [diaryText, setDiaryText] = useState('');
  const [diaryMediaUrl, setDiaryMediaUrl] = useState('');
  const [diaryImage, setDiaryImage] = useState(null);
  const [savingDiary, setSavingDiary] = useState(false);
  const [deletingDiary, setDeletingDiary] = useState(null);
  const diaryImageRef = useRef(null);

  // Payout state
  const [payoutInfo, setPayoutInfo] = useState({ paypal_email: '', bank_name: '', iban: '' });
  const [savingPayout, setSavingPayout] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [commissionData, setCommissionData] = useState({ pending: 0, paid_lifetime: 0, history: [], earns_cash: false, is_pro: false, has_paypal: false });

  useEffect(() => {
    const fetchMessages = () => {
      api.getMessages().then(data => {
        setMessages(data.thread || []);
        setHasUnreadMsg(data.has_unread || false);
      }).catch(() => {});
    };
    fetchMessages();
    api.getDiary().then(data => setDiaryPosts(data || [])).catch(() => {});
    api.getReferrals().then(data => {
      if (data?.payout_info) setPayoutInfo(data.payout_info);
      setCommissionData({
        pending: data?.commission_pending || 0,
        paid_lifetime: data?.commission_paid_lifetime || 0,
        history: data?.commission_history || [],
        earns_cash: data?.earns_cash || false,
        is_pro: data?.is_pro || false,
        has_paypal: data?.has_paypal || false,
      });
    }).catch(() => {});
    api.getAffiliateStatus().then(data => {
      setIsAffiliate(data?.is_affiliate || false);
      if (data?.payout_info) setPayoutInfo(data.payout_info);
    }).catch(() => {});
    // Auto-poll messages every 15 seconds
    const interval = setInterval(fetchMessages, 15000);
    // Load all Google Fonts for previews
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = ALL_FONTS_URL;
    document.head.appendChild(link);
    return () => { clearInterval(interval); document.head.removeChild(link); };
  }, []);

  // Map existing links by platform id (first match wins)
  const linksByPlatform = useMemo(() => {
    const map = {};
    (links || []).forEach(link => {
      if (link.platform && link.platform !== 'custom' && !map[link.platform]) {
        map[link.platform] = link;
      }
    });
    return map;
  }, [links]);

  // Filtered platforms for search (exclude 'custom')
  const filteredPlatforms = useMemo(() => {
    const all = socialPlatforms.filter(p => p.id !== 'custom');
    const q = platformSearch.trim().toLowerCase();
    const filtered = q ? all.filter(p => p.name.toLowerCase().includes(q)) : all;
    // Sort: filled-in platforms first, then by original order
    return [...filtered].sort((a, b) => {
      const aFilled = !!linksByPlatform[a.id];
      const bFilled = !!linksByPlatform[b.id];
      if (aFilled && !bFilled) return -1;
      if (!aFilled && bFilled) return 1;
      return 0;
    });
  }, [platformSearch, linksByPlatform]);

  const activeCount = Object.keys(linksByPlatform).length + (links || []).filter(l => l.platform === 'custom').length;

  /* ── Handlers ── */
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, WebP images allowed'); return;
    }
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const result = await api.uploadImage(type, file);
      if (type === 'profilePicture') setProfilePicture(result.path);
      await refreshUser();
      toast.success('Image uploaded!');
    } catch (err) { toast.error(err.message); }
    finally { setUploading(prev => ({ ...prev, [type]: false })); e.target.value = ''; }
  };

  const handlePlatformInputChange = (platformId, value) => {
    setPlatformInputs(prev => ({ ...prev, [platformId]: value }));
    if (pendingConfirm === platformId) setPendingConfirm(null);
  };

  const handlePlatformCheck = (platformId) => {
    const url = (platformInputs[platformId] || '').trim();
    if (!url) { toast.error('Please enter a URL'); return; }
    if (!validateUrl(url)) { toast.error('Please enter a valid URL'); return; }
    setPendingConfirm(platformId);
  };

  const celebrateFirstLink = () => {
    const colors = ['#0055FF', '#3377FF', '#FFD166', '#06D6A0', '#EF476F'];
    confetti({ particleCount: 130, spread: 80, origin: { y: 0.6 }, zIndex: 9999, colors });
    setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, zIndex: 9999, colors }), 250);
    setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, zIndex: 9999, colors }), 400);
  };

  const handlePlatformConfirm = async (platformId) => {
    const url = (platformInputs[platformId] || '').trim();
    const platform = socialPlatforms.find(p => p.id === platformId);
    if (!platform || !url) return;

    setSavingPlatform(platformId);
    try {
      const wasFirst = links.length === 0;
      const linkData = {
        platform: platform.id,
        platformName: platform.name,
        url,
        title: platform.name,
        color: platform.color
      };
      const savedLink = await api.addLink(linkData);
      setLinks(prev => [...prev, savedLink]);
      setPlatformInputs(prev => { const n = { ...prev }; delete n[platformId]; return n; });
      setPendingConfirm(null);
      await refreshUser();
      if (wasFirst) {
        celebrateFirstLink();
        toast.success('First link added — your profile is live! 🎉');
      } else {
        toast.success(`${platform.name} link added!`);
      }
    } catch (err) { toast.error(err.message); }
    finally { setSavingPlatform(null); }
  };

  const handlePlatformCancel = (platformId) => {
    setPendingConfirm(null);
  };

  const handleRemoveLink = async (id) => {
    try {
      await api.removeLink(id);
      setLinks(prev => prev.filter(link => link.id !== id));
      await refreshUser();
      toast.success('Link removed');
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleEmbed = async (linkId, currentlyOn) => {
    const newVal = !currentlyOn;
    try {
      await api.toggleEmbed(linkId, newVal);
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, embed_enabled: newVal } : l));
      await refreshUser();
      toast.success(newVal ? 'Embed turned on' : 'Embed turned off');
    } catch (err) { toast.error(err.message); }
  };

  const handleSchedule = async (linkId, start, end) => {
    try {
      await api.updateSchedule(linkId, start, end);
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, scheduled_start: start, scheduled_end: end } : l));
      await refreshUser();
      toast.success(start || end ? 'Schedule set' : 'Schedule cleared');
    } catch (err) { toast.error(err.message); }
  };

  const handleCategoryChange = async (linkId, category) => {
    try {
      await api.updateCategory(linkId, category);
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, category } : l));
      await refreshUser();
    } catch (err) { toast.error(err.message); }
  };


  const handleAddCustomLink = async (data) => {
    const customPlatform = socialPlatforms.find(p => p.id === 'custom');
    const linkData = {
      platform: 'custom',
      platformName: data.title || 'Custom Link',
      url: data.url,
      title: data.title || 'Custom Link',
      color: customPlatform?.color || '#8B5CF6',
      description: data.description,
      cta_text: data.cta_text,
      cta_color: data.cta_color,
    };
    try {
      const wasFirst = links.length === 0;
      const savedLink = await api.addLink(linkData);
      // If an image was uploaded, attach it now (separate multipart endpoint)
      let finalLink = savedLink;
      if (data.image_file) {
        try {
          const imgRes = await api.uploadLinkImage(savedLink.id, data.image_file);
          finalLink = { ...savedLink, image_url: imgRes.url };
        } catch (imgErr) {
          toast.warning('Link added, but image upload failed: ' + (imgErr.message || ''));
        }
      }
      setLinks(prev => [...prev, finalLink]);
      await refreshUser();
      if (wasFirst) {
        celebrateFirstLink();
        toast.success('First link added — your profile is live! 🎉');
      } else {
        toast.success('Custom link card added!');
      }
    } catch (err) { toast.error(err.message); throw err; }
  };

  const handleUpdateLink = async (linkId, patch) => {
    try {
      await api.updateLink(linkId, patch);
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, ...patch } : l));
      await refreshUser();
      toast.success('Link updated');
    } catch (err) { toast.error(err.message); throw err; }
  };

  const handleLinkImageUpload = async (linkId, file) => {
    try {
      const res = await api.uploadLinkImage(linkId, file);
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, image_url: res.url } : l));
      await refreshUser();
      toast.success('Image updated');
    } catch (err) { toast.error(err.message); throw err; }
  };

  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dest = result.destination.index;
    if (src === dest) return;

    const reordered = Array.from(links);
    const [moved] = reordered.splice(src, 1);
    reordered.splice(dest, 0, moved);
    setLinks(reordered);

    try {
      await api.reorderLinks(reordered.map(l => l.id));
      await refreshUser();
    } catch (err) {
      setLinks(links);
      toast.error('Failed to reorder links');
    }
  }, [links, refreshUser]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name, bio: formData.bio, theme: formData.theme,
        font: formData.font, animation: formData.animation,
        button_style: formData.button_style, layout: formData.layout,
        accent_color: formData.accent_color, wallpaper: formData.wallpaper,
        profile_music_url: formData.profile_music_url,
        countdown_date: formData.countdown_date, countdown_label: formData.countdown_label,
        email_signup_enabled: formData.email_signup_enabled,
        email_signup_title: formData.email_signup_title,
        email_signup_subtitle: formData.email_signup_subtitle,
        email_signup_button: formData.email_signup_button,
        tip_jar_enabled: formData.tip_jar_enabled, tip_jar_paypal: formData.tip_jar_paypal,
        tip_jar_title: formData.tip_jar_title,
        tip_jar_subtitle: formData.tip_jar_subtitle,
        tip_jar_currency: formData.tip_jar_currency,
        tip_jar_show_balance: formData.tip_jar_show_balance,
        tip_jar_balance: formData.tip_jar_balance,
        tip_jar_goal: formData.tip_jar_goal,
        avatar_shape: formData.avatar_shape,
        link_card_style: formData.link_card_style,
        bg_effect: formData.bg_effect,
        hide_branding: formData.hide_branding,
        orbit_enabled: formData.orbit_enabled,
        orbit_speed: formData.orbit_speed,
        orbit_direction: formData.orbit_direction,
        orbit_platforms: formData.orbit_platforms,
        orbit_spotlight_color: formData.orbit_spotlight_color,
        custom_wallpaper_mode: formData.custom_wallpaper_mode,
        custom_wallpaper_opacity: formData.custom_wallpaper_opacity,
        custom_wallpaper_blur: formData.custom_wallpaper_blur,
        custom_wallpaper_scale: formData.custom_wallpaper_scale,
        stickers: formData.stickers
      };
      if (!isProUser) {
        // Never send Pro-only values from a Free account — the backend 402s on
        // ANY Pro value in the payload (even a leftover from an expired trial),
        // which used to block the whole save. Omitted fields keep their stored value.
        if (!FREE_OPTIONS.themes.includes(payload.theme)) delete payload.theme;
        if (!FREE_OPTIONS.fonts.includes(payload.font)) delete payload.font;
        if (!FREE_OPTIONS.animations.includes(payload.animation)) delete payload.animation;
        if (!FREE_OPTIONS.buttonStyles.includes(payload.button_style)) delete payload.button_style;
        if (!FREE_OPTIONS.layouts.includes(payload.layout)) delete payload.layout;
        if (payload.accent_color) delete payload.accent_color;
        if (payload.wallpaper && payload.wallpaper !== 'none') delete payload.wallpaper;
        if (payload.profile_music_url) delete payload.profile_music_url;
        if (payload.countdown_date) delete payload.countdown_date;
        if (payload.email_signup_enabled) delete payload.email_signup_enabled;
        if (payload.tip_jar_enabled) delete payload.tip_jar_enabled;
        if (payload.avatar_shape && payload.avatar_shape !== 'round') delete payload.avatar_shape;
        if (payload.link_card_style && payload.link_card_style !== 'classic') delete payload.link_card_style;
        if (payload.bg_effect && payload.bg_effect !== 'none') delete payload.bg_effect;
        if (payload.hide_branding) delete payload.hide_branding;
        if (payload.orbit_enabled) delete payload.orbit_enabled;
        if (payload.stickers?.length) delete payload.stickers;
        delete payload.custom_wallpaper_mode;
      }
      await api.updateProfile(payload);
      await refreshUser();
      toast.success('Profile updated successfully!');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteImage = async (type) => {
    try {
      await api.deleteImage(type);
      setProfilePicture(null);
      await refreshUser();
      toast.success('Image removed');
    } catch (err) { toast.error(err.message); }
  };

  const handleSendMessage = async () => {
    if (!contactMsg.trim()) return;
    setSendingMsg(true);
    try {
      await api.sendMessage(contactMsg.trim());
      setMessages([...messages, { sender: 'user', text: contactMsg.trim(), timestamp: new Date().toISOString() }]);
      setContactMsg('');
      toast.success('Message sent!');
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { toast.error(err.message); }
    finally { setSendingMsg(false); }
  };

  const handleCreateDiaryPost = async () => {
    if (!diaryText.trim() && !diaryMediaUrl.trim()) {
      toast.error('Add some text or a media link');
      return;
    }
    if (diaryMediaUrl.trim() && !validateUrl(diaryMediaUrl.trim())) {
      toast.error('Please enter a valid URL');
      return;
    }
    setSavingDiary(true);
    try {
      const post = await api.createDiaryPost({
        text: diaryText.trim(),
        media_url: diaryMediaUrl.trim() || null
      });
      // Upload image if selected
      if (diaryImage && post.id) {
        const imgResult = await api.uploadDiaryImage(post.id, diaryImage);
        post.image_path = imgResult.path;
        post.image_url = imgResult.image_url;
      }
      setDiaryPosts(prev => [post, ...prev]);
      setDiaryText('');
      setDiaryMediaUrl('');
      setDiaryImage(null);
      if (diaryImageRef.current) diaryImageRef.current.value = '';
      toast.success('Post added to your fan page!');
    } catch (err) { toast.error(err.message); }
    finally { setSavingDiary(false); }
  };

  const handleDeleteDiaryPost = async (postId) => {
    setDeletingDiary(postId);
    try {
      await api.deleteDiaryPost(postId);
      setDiaryPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (err) { toast.error(err.message); }
    finally { setDeletingDiary(null); }
  };

  const handleSavePayout = async () => {
    if (!payoutInfo.paypal_email && !payoutInfo.iban) {
      toast.error('Please enter PayPal email or bank details');
      return;
    }
    setSavingPayout(true);
    try {
      await api.updatePayoutInfo(payoutInfo);
      toast.success('Payout details saved!');
    } catch (err) { toast.error(err.message); }
    finally { setSavingPayout(false); }
  };

  if (!user) return null;

  const profilePicUrl = getImageUrl(profilePicture);

  return (
    <div className="min-h-screen bg-gradient-subtle" data-testid="settings-page">
      {/* Nav */}
      <nav className="bg-background/80 backdrop-blur-lg border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
              <div className="flex items-center space-x-2">
                <MonkeyLogo size={32} />
                <span className="text-xl font-bold">Settings</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {/* Universal triple-view preview toggle: Desktop | Mobile | Open */}
              <div className="inline-flex items-center rounded-md border border-border bg-background overflow-hidden" data-testid="preview-toggle">
                <button
                  type="button"
                  onClick={() => { setPreviewMode('desktop'); setShowPreview(true); }}
                  className={`p-2 transition-colors ${showPreview && previewMode === 'desktop' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  title="Desktop preview"
                  aria-label="Desktop preview"
                  data-testid="preview-desktop-btn">
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { setPreviewMode('mobile'); setShowPreview(true); }}
                  className={`p-2 border-l border-border transition-colors ${showPreview && previewMode === 'mobile' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  title="Mobile preview"
                  aria-label="Mobile preview"
                  data-testid="preview-mobile-btn">
                  <Smartphone className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => window.open(`/${user.username}`, '_blank', 'noopener,noreferrer')}
                  className="p-2 border-l border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  title="Open public profile in new tab"
                  aria-label="Open public profile in new tab"
                  data-testid="preview-open-btn">
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── Sticky Tab Navigation ─── */}
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-lg border-b border-border shadow-sm">
        <div className="max-w-4xl mx-auto px-2 sm:px-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide" data-testid="settings-tab-nav">
            {[
              { id: 'profile', label: 'Profile', icon: UserIcon },
              { id: 'appearance', label: 'Appearance', icon: Paintbrush },
              { id: 'links', label: 'Links & Posts', icon: LinkIcon },
              { id: 'widgets', label: 'Widgets', icon: Gift },
              { id: 'account', label: 'Account', icon: SettingsIcon, dot: hasUnreadMsg },
            ].map((t) => {
              const Ico = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setActiveTab(t.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                  data-testid={`settings-tab-${t.id}`}
                >
                  <Ico className="w-4 h-4" />
                  <span>{t.label}</span>
                  {t.dot && (
                    <span className="absolute top-2 right-1 w-2 h-2 rounded-full bg-destructive" data-testid={`settings-tab-${t.id}-dot`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-all duration-300 ${showPreview ? (previewMode === 'desktop' ? 'lg:mr-[640px] lg:max-w-2xl' : 'lg:mr-[420px] lg:max-w-3xl') : ''}`}>

        {/* ─────────────── PROFILE TAB ─────────────── */}
        <div className={activeTab === 'profile' ? '' : 'hidden'} data-testid="tab-content-profile">

        {/* ─── Profile Section ─── */}
        <Card className="p-6 mb-6" data-testid="profile-section">
          <h2 className="text-2xl font-bold mb-6">Profile Information</h2>
          <div className="mb-6">
            <Label>Profile Picture</Label>
            <div className="mt-2 flex items-center space-x-4">
              {profilePicUrl ? (
                <div className="relative">
                  <img src={profilePicUrl} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-background shadow-lg" />
                  <Button size="icon" variant="destructive" className="absolute -top-2 -right-2 w-8 h-8"
                    onClick={() => handleDeleteImage('profilePicture')} data-testid="delete-avatar-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div onClick={() => !uploading.profilePicture && profilePicRef.current?.click()}
                  className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                  data-testid="upload-avatar-area">
                  {uploading.profilePicture ? <Loader2 className="w-6 h-6 text-primary animate-spin" /> : <Upload className="w-6 h-6 text-muted-foreground" />}
                </div>
              )}
              <div>
                <Button variant="outline" size="sm" disabled={uploading.profilePicture}
                  onClick={() => profilePicRef.current?.click()} data-testid="upload-avatar-btn">
                  {uploading.profilePicture ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {profilePicUrl ? 'Change' : 'Upload'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">Max 5MB (JPEG, PNG, GIF, WebP)</p>
              </div>
              <input ref={profilePicRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => handleFileUpload(e, 'profilePicture')} />
            </div>
          </div>
          <div className="mb-4">
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name" className="mt-2" data-testid="name-input" />
          </div>
          <div className="mb-4">
            <Label htmlFor="username">Username</Label>
            <div className="mt-2 flex items-center space-x-2">
              <span className="text-muted-foreground">linkmonkey.io/</span>
              <Input id="username" value={user.username} disabled className="flex-1" data-testid="username-input" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Username cannot be changed</p>
          </div>
          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell people about yourself..." className="mt-2 min-h-[100px]" maxLength={200} data-testid="bio-input" />
            <p className="text-xs text-muted-foreground mt-1">{formData.bio.length}/200 characters</p>
          </div>
        </Card>

        </div>{/* ─────────────── /PROFILE TAB ─────────────── */}

        {/* ─────────────── APPEARANCE TAB ─────────────── */}
        <div className={activeTab === 'appearance' ? '' : 'hidden'} data-testid="tab-content-appearance">

        {/* ─── Appearance Section (Theme + Customise merged) ─── */}
        <Card className="p-6 mb-6" data-testid="customise-section">
          <button className="w-full flex items-center justify-between" onClick={() => setShowCustomise(!showCustomise)} data-testid="customise-toggle-btn">
            <div className="flex items-center space-x-2">
              <Paintbrush className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Appearance</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:inline">{THEME_LIST.find(t => t.id === formData.theme)?.name || 'Default'}</span>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showCustomise ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {showCustomise && (
            <div className="mt-5 space-y-7">
              {/* Theme picker */}
              <div data-testid="theme-section">
                <Label className="flex items-center gap-2 mb-3"><Palette className="w-4 h-4" /> Theme</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Choose how your public profile looks to visitors.
                  {!isProUser && <> Options marked <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#0055FF] text-white align-middle"><CrownIcon className="w-2.5 h-2.5" /> Pro</span> need the Pro plan.</>}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {THEME_LIST.map((theme) => {
                    const isSelected = formData.theme === theme.id;
                    const proOption = !FREE_OPTIONS.themes.includes(theme.id);
                    return (
                      <button key={theme.id} type="button" onClick={() => pickOption('theme', theme.id, proOption, `The ${theme.name} theme`)}
                        className={`relative rounded-xl p-1 transition-all border-2 ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-muted-foreground/30'}`}
                        data-testid={`theme-${theme.id}`}>
                        {proOption && <ProPill className="top-2 left-2" />}
                        <div className="rounded-lg overflow-hidden" style={{ background: theme.preview.bg }}>
                          <div className="h-6 w-full" style={{ background: theme.preview.accent, opacity: 0.3 }} />
                          <div className="p-3 space-y-1.5">
                            <div className="w-8 h-8 rounded-full mx-auto" style={{ background: theme.preview.accent }} />
                            <div className="h-2 w-12 rounded mx-auto" style={{ background: theme.preview.text, opacity: 0.7 }} />
                            <div className="h-1.5 w-16 rounded mx-auto" style={{ background: theme.preview.text, opacity: 0.3 }} />
                            <div className="space-y-1 mt-2">
                              <div className="h-5 rounded" style={{ background: theme.preview.card, border: `1px solid ${theme.preview.text}22` }} />
                              <div className="h-5 rounded" style={{ background: theme.preview.card, border: `1px solid ${theme.preview.text}22` }} />
                            </div>
                          </div>
                        </div>
                        <p className="text-xs font-medium mt-2 text-center truncate">{theme.name}</p>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="h-px bg-border/60" />

              {/* Font */}
              <div>
                <Label className="flex items-center gap-2 mb-3"><Type className="w-4 h-4" /> Font</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FONT_OPTIONS.map(font => {
                    const proOption = !FREE_OPTIONS.fonts.includes(font.id);
                    return (
                    <button key={font.id} onClick={() => pickOption('font', font.id, proOption, `The ${font.name} font`)}
                      className={`relative p-3 rounded-lg border-2 text-left transition-all ${formData.font === font.id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                      data-testid={`font-${font.id}`}>
                      {proOption && <ProPill />}
                      <span className="text-sm font-medium block" style={{ fontFamily: font.family }}>{font.name}</span>
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: font.family }}>Aa Bb Cc 123</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Animation */}
              <div>
                <Label className="mb-3 block">Entrance Animation</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ANIMATION_OPTIONS.map(anim => {
                    const proOption = !FREE_OPTIONS.animations.includes(anim.id);
                    return (
                    <button key={anim.id} onClick={() => pickOption('animation', anim.id, proOption, `The ${anim.name} animation`)}
                      className={`relative p-3 rounded-lg border-2 text-left transition-all ${formData.animation === anim.id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                      data-testid={`anim-${anim.id}`}>
                      {proOption && <ProPill />}
                      <span className="text-sm font-medium block">{anim.name}</span>
                      <span className="text-xs text-muted-foreground">{anim.description}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Button Style */}
              <div>
                <Label className="mb-3 block">Icon Button Style</Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {BUTTON_STYLE_OPTIONS.map(style => {
                    const proOption = !FREE_OPTIONS.buttonStyles.includes(style.id);
                    return (
                    <button key={style.id} onClick={() => pickOption('button_style', style.id, proOption, `The ${style.name} button style`)}
                      className={`relative p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${formData.button_style === style.id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                      data-testid={`btn-style-${style.id}`}>
                      {proOption && <ProPill className="top-1 right-1" />}
                      <div className="w-10 h-10 bg-primary/20 flex items-center justify-center"
                        style={{ borderRadius: style.borderRadius, border: style.border ? '2px solid hsl(var(--primary) / 0.4)' : 'none', opacity: style.outline ? 1 : 0.8, background: style.outline ? 'transparent' : undefined }}>
                        <div className="w-4 h-4 rounded-sm bg-primary/60" />
                      </div>
                      <span className="text-[10px] font-medium">{style.name}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Layout */}
              <div>
                <Label className="mb-3 block">Profile Layout</Label>
                <div className="grid grid-cols-2 gap-2">
                  {LAYOUT_OPTIONS.map(layout => {
                    const proOption = !FREE_OPTIONS.layouts.includes(layout.id);
                    return (
                    <button key={layout.id} onClick={() => pickOption('layout', layout.id, proOption, `The ${layout.name} layout`)}
                      className={`relative p-3 rounded-lg border-2 text-left transition-all ${formData.layout === layout.id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                      data-testid={`layout-${layout.id}`}>
                      {proOption && <ProPill />}
                      <span className="text-sm font-medium block">{layout.name}</span>
                      <span className="text-xs text-muted-foreground">{layout.description}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <Label className="mb-3 flex items-center gap-2">Custom Accent Colour
                  {!isProUser && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#0055FF] text-white"><CrownIcon className="w-2.5 h-2.5" /> Pro</span>}
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Override the theme's accent colour. Leave empty to use the theme default.</p>
                {!isProUser ? (
                  <button type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('upgrade-required', { detail: { message: 'Custom accent colour is a Pro feature. Upgrade at /pricing' } }))}
                    className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-border text-left hover:border-[#0055FF]/50 transition-colors w-full sm:w-auto"
                    data-testid="accent-color-locked">
                    <div className="w-10 h-10 rounded-lg border border-border bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 opacity-60" />
                    <span className="text-sm text-muted-foreground">Unlock any accent colour with <strong className="text-foreground">Pro</strong></span>
                  </button>
                ) : (
                <div className="flex items-center gap-3">
                  <input type="color" value={formData.accent_color || '#8B5CF6'}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-border"
                    data-testid="accent-color-picker" />
                  <Input value={formData.accent_color} onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    placeholder="e.g. #FF5500" className="h-10 w-32 text-sm font-mono" data-testid="accent-color-input" />
                  {formData.accent_color && (
                    <Button variant="ghost" size="sm" onClick={() => setFormData({ ...formData, accent_color: '' })}>
                      <X className="w-3 h-3 mr-1" /> Reset
                    </Button>
                  )}
                </div>
                )}
              </div>

              {/* Wallpaper */}
              <div>
                <Label className="mb-3 block">Background Wallpaper</Label>
                <p className="text-xs text-muted-foreground mb-2">Add floating decorative elements to your profile background</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {WALLPAPER_OPTIONS.map(wp => {
                    const proOption = !FREE_OPTIONS.wallpapers.includes(wp.id);
                    return (
                    <button key={wp.id} onClick={() => pickOption('wallpaper', wp.id, proOption, `The ${wp.name} wallpaper`)}
                      className={`relative p-3 rounded-lg border-2 text-center transition-all ${formData.wallpaper === wp.id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                      data-testid={`wallpaper-${wp.id}`}>
                      {proOption && <ProPill />}
                      {wp.emoji && <span className="text-2xl block mb-1">{wp.emoji}</span>}
                      <span className="text-xs font-medium block">{wp.name}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* ─── v2 Customisation ─── */}
              <V2CustomisationBlock formData={formData} setFormData={setFormData} isPro={user?.subscription?.is_pro} links={links} />
            </div>
          )}
        </Card>

        {/* ─── Stickers (Pro) ─── */}
        <Card className="p-6 mb-6" data-testid="stickers-section">
          <div className="flex items-center gap-2 mb-1">
            <StickerCardIcon className="w-6 h-6 text-primary" />
            <h2 className="text-lg font-bold">Stickers</h2>
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-950/50 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
              <CrownIcon className="w-2.5 h-2.5" /> Pro
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Drop stickers anywhere on your profile — drag to position, resize and rotate. Don't forget to hit Save!
          </p>
          <StickerEditor
            stickers={formData.stickers || []}
            onChange={(updater) => setFormData((prev) => ({
              ...prev,
              stickers: typeof updater === 'function' ? updater(prev.stickers || []) : updater,
            }))}
            isPro={!!user?.subscription?.is_pro}
            selectedId={selectedStickerId}
            onSelect={setSelectedStickerId}
            previewOpen={showPreview}
            onRequestPreview={() => setShowPreview(true)}
          />
        </Card>

        </div>{/* ─────────────── /APPEARANCE TAB ─────────────── */}

        {/* ─────────────── WIDGETS TAB ─────────────── */}
        <div className={activeTab === 'widgets' ? '' : 'hidden'} data-testid="tab-content-widgets">

        {/* ─── Widgets Section ─── */}
        <Card className="p-6 mb-6" data-testid="widgets-section">
          <div className="flex items-center gap-2 mb-5">
            <Timer className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Widgets</h2>
          </div>

          <div className="space-y-5">
            {/* Profile Music */}
            <div className="p-4 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Music className="w-4 h-4 text-primary" />
                <Label className="font-semibold">Profile Music</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Paste a Spotify or SoundCloud track URL — it plays automatically when visitors land on your page.</p>
              <Input value={formData.profile_music_url}
                onChange={(e) => setFormData({ ...formData, profile_music_url: e.target.value })}
                placeholder="https://open.spotify.com/track/... or https://soundcloud.com/artist/track"
                className="h-9 text-sm" data-testid="music-url-input" />
              {formData.profile_music_url && (
                <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={() => setFormData({ ...formData, profile_music_url: '' })}>
                  <X className="w-3 h-3 mr-1" /> Remove music
                </Button>
              )}
            </div>

            {/* Countdown Timer */}
            <div className="p-4 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-4 h-4 text-orange-500" />
                <Label className="font-semibold">Countdown Timer</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Add a countdown to your profile for drops, events, or launches.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={formData.countdown_label}
                  onChange={(e) => setFormData({ ...formData, countdown_label: e.target.value })}
                  placeholder="e.g. New Album Drops In..." className="h-9 text-sm flex-1"
                  data-testid="countdown-label-input" />
                <Input type="datetime-local" value={formData.countdown_date?.slice(0, 16) || ''}
                  onChange={(e) => setFormData({ ...formData, countdown_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  className="h-9 text-sm w-auto" data-testid="countdown-date-input" />
              </div>
              {formData.countdown_date && (
                <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={() => setFormData({ ...formData, countdown_date: '', countdown_label: '' })}>
                  <X className="w-3 h-3 mr-1" /> Remove countdown
                </Button>
              )}
            </div>

            {/* Email Signup */}
            <div className="p-4 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-green-500" />
                <Label className="font-semibold">Email Signup</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Let visitors subscribe to your mailing list directly from your profile.</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFormData({ ...formData, email_signup_enabled: !formData.email_signup_enabled })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.email_signup_enabled ? 'bg-green-500' : 'bg-muted'}`}
                  data-testid="email-signup-toggle">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.email_signup_enabled ? 'translate-x-5' : ''}`} />
                </button>
                <span className="text-sm">{formData.email_signup_enabled ? 'Enabled — signup form shows on your profile' : 'Disabled'}</span>
              </div>
              {formData.email_signup_enabled && (
                <div className="mt-3 space-y-2 pl-1 border-l-2 border-green-500/30 pl-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Title</Label>
                    <Input value={formData.email_signup_title || ''}
                      onChange={(e) => setFormData({ ...formData, email_signup_title: e.target.value })}
                      placeholder="Stay Connected" className="h-9 text-sm mt-1" data-testid="email-signup-title-input" maxLength={80} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Subtitle</Label>
                    <Input value={formData.email_signup_subtitle || ''}
                      onChange={(e) => setFormData({ ...formData, email_signup_subtitle: e.target.value })}
                      placeholder="Get updates straight to your inbox" className="h-9 text-sm mt-1" data-testid="email-signup-subtitle-input" maxLength={180} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Button text</Label>
                    <Input value={formData.email_signup_button || ''}
                      onChange={(e) => setFormData({ ...formData, email_signup_button: e.target.value })}
                      placeholder="Subscribe" className="h-9 text-sm mt-1" data-testid="email-signup-button-input" maxLength={40} />
                  </div>
                  <p className="text-[11px] text-muted-foreground italic">Leave blank to use defaults.</p>
                </div>
              )}
            </div>

            {/* Link Categories */}
            <div className="p-4 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4 text-purple-500" />
                <Label className="font-semibold">Link Categories</Label>
              </div>
              <p className="text-xs text-muted-foreground">Assign categories to your links (e.g. Music, Social, Shop). Visitors can filter by category on your profile. Set categories on each link below.</p>
            </div>
          </div>
        </Card>

        {/* ─── Tip Jar Section ─── */}
        <Card className="p-6 mb-6" data-testid="revenue-section">
          <div className="flex items-center gap-2 mb-5">
            <DollarSign className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-bold">Monetise</h2>
          </div>

          {/* Tip Jar */}
          <div className="p-4 rounded-xl border border-border mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-pink-500" />
              <Label className="font-semibold">Tip Jar</Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Let visitors send you tips via PayPal directly from your profile.</p>
            <div className="flex items-center gap-3 mb-3">
              <button onClick={() => setFormData({ ...formData, tip_jar_enabled: !formData.tip_jar_enabled })}
                className={`relative w-11 h-6 rounded-full transition-colors ${formData.tip_jar_enabled ? 'bg-pink-500' : 'bg-muted'}`}
                data-testid="tip-jar-toggle">
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.tip_jar_enabled ? 'translate-x-5' : ''}`} />
              </button>
              <span className="text-sm">{formData.tip_jar_enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            {formData.tip_jar_enabled && (
              <>
                <Input value={formData.tip_jar_paypal}
                  onChange={(e) => setFormData({ ...formData, tip_jar_paypal: e.target.value })}
                  placeholder="Your PayPal email for receiving tips" className="h-9 text-sm" data-testid="tip-jar-paypal" />
                <div className="mt-3 space-y-2 pl-3 border-l-2 border-pink-500/30">
                  <div>
                    <Label className="text-xs text-muted-foreground">Title</Label>
                    <Input value={formData.tip_jar_title || ''}
                      onChange={(e) => setFormData({ ...formData, tip_jar_title: e.target.value })}
                      placeholder="Tip Jar" className="h-9 text-sm mt-1" data-testid="tip-jar-title-input" maxLength={80} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Subtitle</Label>
                    <Input value={formData.tip_jar_subtitle || ''}
                      onChange={(e) => setFormData({ ...formData, tip_jar_subtitle: e.target.value })}
                      placeholder={`Support ${user?.name || user?.username || 'me'} with a tip`} className="h-9 text-sm mt-1" data-testid="tip-jar-subtitle-input" maxLength={180} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Currency</Label>
                    <select
                      value={formData.tip_jar_currency || 'EUR'}
                      onChange={(e) => setFormData({ ...formData, tip_jar_currency: e.target.value })}
                      className="w-full h-9 text-sm mt-1 px-3 rounded-md border border-input bg-background"
                      data-testid="tip-jar-currency-select">
                      <option value="EUR">€ Euro (EUR)</option>
                      <option value="USD">$ US Dollar (USD)</option>
                      <option value="GBP">£ British Pound (GBP)</option>
                      <option value="CAD">CA$ Canadian Dollar (CAD)</option>
                      <option value="AUD">A$ Australian Dollar (AUD)</option>
                      <option value="JPY">¥ Japanese Yen (JPY)</option>
                      <option value="CHF">CHF Swiss Franc (CHF)</option>
                      <option value="NZD">NZ$ New Zealand Dollar (NZD)</option>
                    </select>
                  </div>
                  <p className="text-[11px] text-muted-foreground italic">Leave title/subtitle blank to use defaults.</p>
                </div>

                {/* Fundraising progress block */}
                <div className="mt-4 p-3 rounded-lg border border-pink-500/30 bg-pink-500/[0.04] space-y-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setFormData({ ...formData, tip_jar_show_balance: !formData.tip_jar_show_balance })}
                      className={`relative w-9 h-5 rounded-full transition-colors ${formData.tip_jar_show_balance ? 'bg-pink-500' : 'bg-muted'}`}
                      data-testid="tip-jar-show-balance-toggle">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${formData.tip_jar_show_balance ? 'translate-x-4' : ''}`} />
                    </button>
                    <Label className="text-sm font-medium cursor-pointer" onClick={() => setFormData({ ...formData, tip_jar_show_balance: !formData.tip_jar_show_balance })}>
                      Show fundraising progress
                    </Label>
                  </div>
                  {formData.tip_jar_show_balance && (
                    <>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Balance auto-updates when a tip clears in PayPal. You can also edit it manually here to add offline donations or correct any discrepancy.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Amount raised</Label>
                          <Input
                            type="number" min="0" step="0.01"
                            value={formData.tip_jar_balance ?? ''}
                            onChange={(e) => setFormData({ ...formData, tip_jar_balance: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                            placeholder="0" className="h-9 text-sm mt-1 font-mono" data-testid="tip-jar-balance-input" />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Goal <span className="opacity-60">(optional)</span></Label>
                          <Input
                            type="number" min="0" step="1"
                            value={formData.tip_jar_goal ?? ''}
                            onChange={(e) => setFormData({ ...formData, tip_jar_goal: e.target.value === '' ? null : parseFloat(e.target.value) })}
                            placeholder="e.g. 1000" className="h-9 text-sm mt-1 font-mono" data-testid="tip-jar-goal-input" />
                        </div>
                      </div>
                      {formData.tip_jar_goal > 0 && formData.tip_jar_balance >= 0 && (
                        <div className="pt-1">
                          <div className="h-1.5 rounded-full overflow-hidden bg-muted">
                            <div className="h-full rounded-full bg-pink-500 transition-all"
                              style={{ width: `${Math.min(100, ((formData.tip_jar_balance || 0) / formData.tip_jar_goal) * 100)}%` }} />
                          </div>
                          <p className="text-[10px] mt-1 text-muted-foreground tabular-nums">
                            {Math.round(((formData.tip_jar_balance || 0) / formData.tip_jar_goal) * 100)}% of goal
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <RecentTipsList />
              </>
            )}
          </div>
        </Card>

        </div>{/* ─────────────── /WIDGETS TAB ─────────────── */}

        {/* ─────────────── LINKS TAB ─────────────── */}
        <div className={activeTab === 'links' ? '' : 'hidden'} data-testid="tab-content-links">

        <Card className="p-6 mb-6" data-testid="links-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <LinkIcon className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Your Links</h2>
            </div>
            <Badge variant="secondary" data-testid="active-links-count">{activeCount} active</Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-5">Fill in the URL beside each platform. Click the check to confirm.</p>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={platformSearch}
              onChange={(e) => setPlatformSearch(e.target.value)}
              placeholder="Search platforms..."
              className="pl-9 h-10"
              data-testid="platform-search"
            />
            {platformSearch && (
              <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setPlatformSearch('')}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>

          {/* Platform list */}
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1 scrollbar-thin" data-testid="platform-list">
            {filteredPlatforms.map(platform => (
              <PlatformRow
                key={platform.id}
                platform={platform}
                existingLink={linksByPlatform[platform.id]}
                inputValue={platformInputs[platform.id] || ''}
                isPending={pendingConfirm === platform.id}
                isSaving={savingPlatform === platform.id}
                isEmbedable={!!(linksByPlatform[platform.id] && isEmbeddableUrl(linksByPlatform[platform.id].url))}
                onInputChange={handlePlatformInputChange}
                onCheck={handlePlatformCheck}
                onConfirm={handlePlatformConfirm}
                onCancel={handlePlatformCancel}
                onRemove={handleRemoveLink}
                onToggleEmbed={handleToggleEmbed}
                onSchedule={handleSchedule}
                onCategoryChange={handleCategoryChange}
              />
            ))}
            {filteredPlatforms.length === 0 && (
              <p className="text-center text-muted-foreground py-8 text-sm">No platforms match "{platformSearch}"</p>
            )}
          </div>

          {/* Custom link */}
          <CustomLinkForm onAdd={handleAddCustomLink} />

          <p className="text-xs text-muted-foreground mt-4 text-center">
            {socialPlatforms.length} platforms supported. Embeds (YouTube, Spotify, etc.) are detected automatically.
          </p>
          <div className="mt-3 p-3 rounded-lg bg-muted/40 border border-border/50" data-testid="embed-hint">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Auto-play embeds:</span> To embed a YouTube video, paste a <span className="font-medium">specific video link</span> (e.g. youtube.com/watch?v=...), not a channel URL. Same for Spotify tracks, SoundCloud songs, etc. — use the direct link to the content you want embedded.
            </p>
          </div>
        </Card>

        {/* ─── Reorder Links Section ─── */}
        {links.length > 1 && (
          <Card className="p-6 mb-6" data-testid="reorder-section">
            <div className="flex items-center gap-2 mb-2">
              <GripVertical className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold">Reorder Links</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Drag to reorder how links appear on your profile</p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="links">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2" data-testid="links-list">
                    {links.map((link, index) => {
                      const platform = socialPlatforms.find(p => p.id === link.platform);
                      const Icon = platform?.icon;
                      const embeddable = isEmbeddableUrl(link.url);
                      return (
                        <Draggable key={link.id} draggableId={link.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex flex-col gap-3 p-3 rounded-xl border bg-background transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
                              data-testid={`link-item-${link.id}`}
                            >
                              <div className="flex items-center gap-3 w-full">
                                <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${link.id}`}>
                                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                                </div>
                                {link.image_url ? (
                                  <img src={link.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${link.color}15` }}>
                                    {Icon && <Icon className="w-5 h-5" style={{ color: link.color }} />}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm truncate">{link.title}</p>
                                    {embeddable && <Badge variant="secondary" className="text-[10px] flex-shrink-0">Embed</Badge>}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                                </div>
                                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditingLinkId(editingLinkId === link.id ? null : link.id)} data-testid={`edit-link-${link.id}`}>
                                  {editingLinkId === link.id ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-red-500 hover:text-red-600" onClick={() => handleRemoveLink(link.id)} data-testid={`remove-link-${link.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              {editingLinkId === link.id && (
                                <LinkRichEditor link={link} onSave={handleUpdateLink} onImageUpload={handleLinkImageUpload} onClose={() => setEditingLinkId(null)} isPro={!!user?.subscription?.is_pro} />
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <p className="text-xs text-muted-foreground mt-3 text-center">Drag links to reorder. Order is saved automatically.</p>
          </Card>
        )}

        {/* ─── Fan Page / Diary Section ─── */}
        <Card className="p-6 mb-6" data-testid="diary-section">
          <div className="flex items-center gap-2 mb-2">
            <Newspaper className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Fan Page</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Share videos, songs, and thoughts on your profile. Visitors see this as a feed.</p>
          <p className="text-xs text-muted-foreground mb-5 bg-muted/40 px-3 py-2 rounded-lg">A "Fan Page" button will appear on your public profile once you add your first post.</p>

          {/* New post form */}
          <div className="p-4 rounded-xl border border-border bg-muted/20 mb-5" data-testid="diary-new-post">
            <Textarea
              value={diaryText}
              onChange={(e) => setDiaryText(e.target.value)}
              placeholder="What are you into right now? Share a thought..."
              className="min-h-[80px] mb-3"
              maxLength={500}
              data-testid="diary-text-input"
            />
            <div className="space-y-2 mb-3">
              <Input
                value={diaryMediaUrl}
                onChange={(e) => setDiaryMediaUrl(e.target.value)}
                placeholder="Paste a YouTube, Spotify, or SoundCloud link (optional)"
                className="h-9 text-sm"
                data-testid="diary-media-url"
              />
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => diaryImageRef.current?.click()} data-testid="diary-image-btn">
                  <Image className="w-4 h-4 mr-2" /> {diaryImage ? diaryImage.name : 'Add Image'}
                </Button>
                {diaryImage && (
                  <Button variant="ghost" size="sm" onClick={() => { setDiaryImage(null); if (diaryImageRef.current) diaryImageRef.current.value = ''; }}>
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                )}
                <input ref={diaryImageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setDiaryImage(e.target.files[0]); }} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{diaryText.length}/500</p>
              <Button onClick={handleCreateDiaryPost} disabled={savingDiary || (!diaryText.trim() && !diaryMediaUrl.trim())} data-testid="diary-post-btn">
                {savingDiary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Post
              </Button>
            </div>
          </div>

          {/* Existing posts */}
          {diaryPosts.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm" data-testid="diary-empty">No posts yet. Share something you love!</p>
          ) : (
            <div className="space-y-3" data-testid="diary-posts-list">
              {diaryPosts.map(post => (
                <div key={post.id} className="p-4 rounded-xl border border-border group relative" data-testid={`diary-post-${post.id}`}>
                  {post.text && <p className="text-sm mb-2 whitespace-pre-wrap">{post.text}</p>}
                  {post.image_url && (
                    <img src={getImageUrl(post.image_path || post.image_url)} alt="" className="rounded-lg max-h-64 w-auto mb-2" />
                  )}
                  {post.media_url && (
                    <div className="mb-2">
                      {isEmbeddableUrl(post.media_url) ? (
                        <div className="text-xs text-primary font-medium mb-1">Embedded: {getEmbedLabel(post.media_url)}</div>
                      ) : null}
                      <a href={post.media_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                        {post.media_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString()}</p>
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive/60 hover:text-destructive"
                      onClick={() => handleDeleteDiaryPost(post.id)} disabled={deletingDiary === post.id} data-testid={`diary-delete-${post.id}`}>
                      {deletingDiary === post.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        </div>{/* ─────────────── /LINKS TAB ─────────────── */}

        {/* ─────────────── ACCOUNT TAB ─────────────── */}
        <div className={activeTab === 'account' ? '' : 'hidden'} data-testid="tab-content-account">

        {/* Billing & subscription quick-link */}
        <Card className="p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="settings-quick-links">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm">Billing & subscription</p>
              <p className="text-xs text-muted-foreground">View plan, payment history & renew</p>
            </div>
          </div>
          <Link to="/billing">
            <Button size="sm" variant="outline" data-testid="goto-billing-btn">Manage billing →</Button>
          </Link>
        </Card>

        {/* Email subscribers (Pro) quick-link */}
        <Card className="p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="campaigns-quick-link">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Mail className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <p className="font-semibold text-sm">Email your subscribers <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-600">Pro</span></p>
              <p className="text-xs text-muted-foreground">Send a plain-text update to every confirmed subscriber. 1/day, up to 5,000.</p>
            </div>
          </div>
          <Link to="/campaigns">
            <Button size="sm" variant="outline" data-testid="goto-campaigns-btn">Open composer →</Button>
          </Link>
        </Card>

        {/* ─── Commission History (affiliates only) ─── */}
        {isAffiliate && (commissionData.history.length > 0 || commissionData.pending > 0 || commissionData.paid_lifetime > 0) && (
        <Card className="p-6 mb-6" data-testid="commission-history-section">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-6 h-6 text-emerald-500" />
            <h2 className="text-2xl font-bold">Commission History</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Real money earned from referred users who became Pro. Pending entries become Paid once we send your payout (usually monthly).
          </p>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 p-4">
              <p className="text-[11px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold">Pending</p>
              <p className="text-2xl font-bold tabular-nums text-amber-900 dark:text-amber-100" data-testid="commission-pending">
                €{commissionData.pending.toFixed(2)}
              </p>
              <p className="text-[11px] text-amber-700/70 dark:text-amber-400/70">Owed to you now</p>
            </div>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-4">
              <p className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Paid Lifetime</p>
              <p className="text-2xl font-bold tabular-nums text-emerald-900 dark:text-emerald-100" data-testid="commission-paid">
                €{commissionData.paid_lifetime.toFixed(2)}
              </p>
              <p className="text-[11px] text-emerald-700/70 dark:text-emerald-400/70">Already paid out</p>
            </div>
          </div>

          {commissionData.history.length > 0 ? (
            <div className="space-y-2" data-testid="commission-rows">
              {commissionData.history.slice(0, 25).map((c, i) => (
                <div key={c.invoice_id || i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/30 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    {c.paid_out ? (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium capitalize">
                        {c.plan || 'Subscription'} commission
                        {c.paid_out
                          ? <span className="ml-2 text-[10px] text-emerald-600">Paid{c.paid_out_at ? ` ${new Date(c.paid_out_at).toLocaleDateString('en-IE')}` : ''}</span>
                          : <span className="ml-2 text-[10px] text-amber-600">Pending</span>}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IE', { dateStyle: 'medium' }) : ''}
                        {c.buyer_hint ? ` · from ${c.buyer_hint}` : ''}
                      </p>
                    </div>
                  </div>
                  <span className="font-semibold tabular-nums">€{(c.amount || 0).toFixed(2)}</span>
                </div>
              ))}
              {commissionData.history.length > 25 && (
                <p className="text-[11px] text-muted-foreground pt-2 text-center">
                  Showing latest 25 of {commissionData.history.length}.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No commissions yet. Earn €1 for every monthly Pro and €10 for every yearly Pro you refer.
            </p>
          )}
        </Card>
        )}

        {/* ─── Payout Details Section (affiliates only) ─── */}
        {isAffiliate && (
        <Card className="p-6 mb-6" data-testid="payout-section">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-bold">PayPal Payout</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Earn <strong>€1/month</strong> for every Monthly Pro and <strong>€10</strong> for every Yearly Pro you refer.
            We pay exclusively via PayPal — make sure your PayPal email is correct.
          </p>
          {commissionData.earns_cash ? (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-3 mb-5">
              <p className="text-[12px] text-emerald-900 dark:text-emerald-100">
                <strong>✓ You're earning.</strong> Your referral link is active, commissions accrue automatically.
              </p>
            </div>
          ) : !commissionData.is_pro ? (
            <div className="rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 p-3 mb-5">
              <p className="text-[12px] text-purple-900 dark:text-purple-100">
                <strong>Pro required.</strong> Only Pro members earn commissions. Free users still get signups tracked, but to convert those into cash, <Link to="/pricing" className="underline font-semibold">upgrade to Pro</Link>.
              </p>
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 p-3 mb-5">
              <p className="text-[12px] text-amber-900 dark:text-amber-100">
                <strong>Add PayPal to start earning.</strong> Without it, signups are still tracked but no commission is accrued. Once added, your referral link activates instantly.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="paypal_email">PayPal Email <span className="text-red-500">*</span></Label>
              <Input id="paypal_email" type="email" value={payoutInfo.paypal_email}
                onChange={(e) => setPayoutInfo({ ...payoutInfo, paypal_email: e.target.value })}
                placeholder="your@paypal.com" className="mt-1" data-testid="payout-paypal" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Must be a verified PayPal account. We pay out monthly via PayPal Mass Pay.
              </p>
            </div>
            <Button onClick={handleSavePayout} disabled={savingPayout} className="w-full" data-testid="save-payout-btn">
              {savingPayout ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save PayPal Email
            </Button>
          </div>
        </Card>
        )}

        {/* ─── Privacy & Data (GDPR) ─── */}
        <Card className="p-6 mb-6" data-testid="privacy-data-section">
          <div className="flex items-center space-x-2 mb-4">
            <Download className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Privacy & Data</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Your data, your rights. Export a full copy of everything we hold on you, or permanently delete your account.
            Read our <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground" data-testid="settings-privacy-link">Privacy Policy</Link> for details.
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={async () => {
                try {
                  const data = await api.exportMyData();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `linkmonkey-export-${new Date().toISOString().slice(0,10)}.json`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success('Your data export has been downloaded.');
                } catch (e) {
                  toast.error(e.message || 'Export failed');
                }
              }}
              data-testid="export-data-btn"
            >
              <Download className="w-4 h-4 mr-3 shrink-0" />
              <div className="text-left">
                <div className="font-medium text-sm">Download my data</div>
                <div className="text-[11px] text-muted-foreground font-normal">GDPR Article 20 — full JSON export</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-3 border-destructive/30 hover:bg-destructive/5 hover:text-destructive hover:border-destructive/50"
              onClick={async () => {
                const confirmText = window.prompt(
                  'This will permanently delete your LinkMonkey account, cancel any active subscription, and erase all your data. This cannot be undone.\n\nType DELETE to confirm:'
                );
                if (confirmText !== 'DELETE') {
                  if (confirmText !== null) toast.error('Deletion cancelled — confirmation did not match.');
                  return;
                }
                try {
                  await api.deleteAccount();
                  toast.success('Account permanently deleted. Confirmation email sent.');
                  setTimeout(() => { window.location.href = '/'; }, 1500);
                } catch (e) {
                  toast.error(e.message || 'Deletion failed');
                }
              }}
              data-testid="delete-account-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-3 shrink-0" />
              <div className="text-left">
                <div className="font-medium text-sm">Delete my account</div>
                <div className="text-[11px] text-muted-foreground font-normal">Permanent · cancels subscription</div>
              </div>
            </Button>
          </div>
        </Card>

        {/* ─── Contact Section ─── */}
        <Card className={`p-6 ${hasUnreadMsg ? 'ring-2 ring-primary/30' : ''}`} data-testid="contact-section">
          <div className="flex items-center space-x-2 mb-4">
            <MessageSquare className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Contact Us</h2>
            {hasUnreadMsg && (
              <span className="w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center font-bold" data-testid="unread-badge">!</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">Have feedback, an idea, or something not working? Send us a message and we'll get back to you here.</p>

          {messages.length > 0 && (
            <div className="mb-4 max-h-64 overflow-y-auto rounded-lg border border-border p-3 space-y-3 bg-muted/20" data-testid="message-thread"
              onMouseEnter={() => { if (hasUnreadMsg) { api.markMessagesRead().then(() => setHasUnreadMsg(false)).catch(() => {}); } }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted rounded-bl-md'}`}
                    data-testid={`message-${i}`}>
                    <p>{msg.text}</p>
                    <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                      {msg.sender === 'admin' ? 'LinkMonkey Team' : 'You'} — {new Date(msg.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          <div className="flex gap-2">
            <Textarea
              value={contactMsg}
              onChange={(e) => setContactMsg(e.target.value)}
              placeholder="Type your message..."
              className="min-h-[80px] flex-1"
              maxLength={2000}
              data-testid="contact-message-input"
            />
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted-foreground">{contactMsg.length}/2000</p>
            <Button onClick={handleSendMessage} disabled={sendingMsg || !contactMsg.trim()} data-testid="send-message-btn">
              {sendingMsg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Message
            </Button>
          </div>
        </Card>

        </div>{/* ─────────────── /ACCOUNT TAB ─────────────── */}

        <div className="mt-6 sm:hidden">
          <Button onClick={handleSave} className="w-full" size="lg" disabled={saving} data-testid="save-mobile-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <ProfilePreview
          links={links}
          profilePicture={profilePicture}
          formData={formData}
          username={user.username}
          mode={previewMode}
          onClose={() => setShowPreview(false)}
          stickerEdit={user?.subscription?.is_pro ? {
            editable: true,
            selectedId: selectedStickerId,
            onSelect: setSelectedStickerId,
            onChange: (updater) => setFormData((prev) => ({
              ...prev,
              stickers: typeof updater === 'function' ? updater(prev.stickers || []) : updater,
            })),
          } : undefined}
        />
      )}
    </div>
  );
};

export default Settings;
